import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Heart, Lock, Users, FileText, Flag, Download, Trash2, 
  Eye, RefreshCw, LogOut, AlertCircle, CheckCircle, XCircle,
  BarChart3, Search, DollarSign, TrendingUp, Calendar,
  Edit, Save, X, CheckSquare, Square, Layers, Activity, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import Logo from "@/components/Logo";
import { API } from "@/utils/api";

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem("lobe_admin_token");
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("lobe_admin_token");
    setToken("");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  if (!isAuthenticated) {
    return <AdminLogin setIsAuthenticated={setIsAuthenticated} setToken={setToken} />;
  }

  return (
    <div className="min-h-screen bg-[#F4F1EA]" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="bg-white border-b border-[#E2E2E2] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="text-sm text-[#888888] border-l border-[#E2E2E2] pl-4">Admin Panel</span>
          </div>
          <Button 
            variant="ghost" 
            onClick={handleLogout}
            className="text-[#5A5A5A] hover:text-[#C25E5E]"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Admin Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-sm flex-wrap">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#E8A0BF] data-[state=active]:text-white rounded-lg">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="certificates" className="data-[state=active]:bg-[#E8A0BF] data-[state=active]:text-white rounded-lg">
              <FileText className="w-4 h-4 mr-2" />
              Certificates
            </TabsTrigger>
            <TabsTrigger value="purchases" className="data-[state=active]:bg-[#E8A0BF] data-[state=active]:text-white rounded-lg">
              <DollarSign className="w-4 h-4 mr-2" />
              Purchases
            </TabsTrigger>
            <TabsTrigger value="flagged" className="data-[state=active]:bg-[#E8A0BF] data-[state=active]:text-white rounded-lg">
              <Flag className="w-4 h-4 mr-2" />
              Flagged
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <AnalyticsTab token={token} />
          </TabsContent>

          <TabsContent value="certificates">
            <CertificatesTab token={token} flaggedOnly={false} />
          </TabsContent>

          <TabsContent value="purchases">
            <PurchasesTab token={token} />
          </TabsContent>

          <TabsContent value="flagged">
            <CertificatesTab token={token} flaggedOnly={true} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Admin Login Component
const AdminLogin = ({ setIsAuthenticated, setToken }) => {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      const { token } = response.data;
      
      localStorage.setItem("lobe_admin_token", token);
      setToken(token);
      setIsAuthenticated(true);
      toast.success("Welcome to Admin Panel");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFDF7] px-6" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Logo className="justify-center mb-4" />
          <h1 className="text-2xl font-bold text-[#2D2D2D]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Admin Login
          </h1>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-[#E2E2E2]/50">
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                placeholder="Enter username"
                className="h-12"
                data-testid="admin-username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                placeholder="Enter password"
                className="h-12"
                data-testid="admin-password"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary"
              data-testid="admin-login-btn"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Login
                </>
              )}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab = ({ token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    fetchVisitors();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics?token=${token}`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVisitors = async () => {
    try {
      const response = await axios.get(`${API}/admin/visitors?token=${token}`);
      setVisitors(response.data);
    } catch (error) {
      console.error("Failed to fetch visitor stats");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[#E8A0BF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Visitor Stats Section */}
      {visitors && (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#2D2D2D] flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              <Globe className="w-5 h-5 text-[#E8A0BF]" />
              Visitor Statistics
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchVisitors}
              className="text-[#888888]"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-[#E8A0BF]/10 to-[#E8A0BF]/5 rounded-xl p-4 border border-[#E8A0BF]/20">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-[#E8A0BF]" />
                <span className="text-sm text-[#5A5A5A] font-medium">Today</span>
              </div>
              <p className="text-3xl font-bold text-[#2D2D2D]">{visitors.today?.visitors || 0}</p>
              <p className="text-xs text-[#888888]">{visitors.today?.page_views || 0} page views</p>
            </div>
            <div className="bg-gradient-to-br from-[#B4D2E7]/10 to-[#B4D2E7]/5 rounded-xl p-4 border border-[#B4D2E7]/20">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-[#5B9BD5]" />
                <span className="text-sm text-[#5A5A5A] font-medium">This Week</span>
              </div>
              <p className="text-3xl font-bold text-[#2D2D2D]">{visitors.this_week?.visitors || 0}</p>
              <p className="text-xs text-[#888888]">{visitors.this_week?.page_views || 0} page views</p>
            </div>
            <div className="bg-gradient-to-br from-[#4CAF50]/10 to-[#4CAF50]/5 rounded-xl p-4 border border-[#4CAF50]/20">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-[#4CAF50]" />
                <span className="text-sm text-[#5A5A5A] font-medium">This Month</span>
              </div>
              <p className="text-3xl font-bold text-[#2D2D2D]">{visitors.this_month?.visitors || 0}</p>
              <p className="text-xs text-[#888888]">{visitors.this_month?.page_views || 0} page views</p>
            </div>
          </div>
          
          {/* Daily Breakdown */}
          {visitors.daily_breakdown && visitors.daily_breakdown.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#E2E2E2]">
              <h4 className="text-sm font-medium text-[#5A5A5A] mb-3">Last 7 Days</h4>
              <div className="grid grid-cols-7 gap-2">
                {visitors.daily_breakdown.map((day, idx) => (
                  <div key={idx} className="text-center">
                    <p className="text-xs text-[#888888]">{day.day_name.slice(0, 3)}</p>
                    <p className="text-lg font-semibold text-[#2D2D2D]">{day.visitors}</p>
                    <p className="text-xs text-[#888888]">{day.page_views} views</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Certificates" 
          value={analytics?.total_certificates || 0}
          icon={FileText}
          color="bg-[#B4D2E7]"
        />
        <StatCard 
          title="Paid Certificates" 
          value={analytics?.paid_certificates || 0}
          icon={CheckCircle}
          color="bg-[#4CAF50]"
        />
        <StatCard 
          title="Flagged" 
          value={analytics?.flagged_certificates || 0}
          icon={Flag}
          color="bg-[#FF9800]"
        />
        <StatCard 
          title="Conversion Rate" 
          value={`${analytics?.conversion_rate || 0}%`}
          icon={BarChart3}
          color="bg-[#E8A0BF]"
        />
      </div>

      {/* Recent Certificates */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
          Recent Certificates
        </h3>
        <div className="space-y-3">
          {analytics?.recent_certificates?.map((cert) => (
            <div 
              key={cert.id} 
              className="flex items-center justify-between p-3 bg-[#F4F1EA] rounded-lg"
            >
              <div>
                <p className="font-medium text-[#2D2D2D]">
                  {cert.your_name} & {cert.partner_name}
                </p>
                <p className="text-sm text-[#888888] font-mono">{cert.certificate_id}</p>
              </div>
              <div className="flex items-center gap-2">
                {cert.is_paid ? (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Paid</span>
                ) : (
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">Pending</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl shadow-sm p-4">
    <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center mb-3`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <p className="text-2xl font-bold text-[#2D2D2D]">{value}</p>
    <p className="text-sm text-[#888888]">{title}</p>
  </div>
);

// Certificates Tab Component
const CertificatesTab = ({ token, flaggedOnly }) => {
  const [certificates, setCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Bulk selection state
  const [selectedCerts, setSelectedCerts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Edit modal state
  const [editingCert, setEditingCert] = useState(null);
  const [editForm, setEditForm] = useState({ your_name: "", partner_name: "", description: "" });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchCertificates();
  }, [page, flaggedOnly]);

  useEffect(() => {
    if (selectAll) {
      setSelectedCerts(certificates.map(c => c.certificate_id));
    } else {
      setSelectedCerts([]);
    }
  }, [selectAll, certificates]);

  const fetchCertificates = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/certificates?token=${token}&page=${page}&limit=20&flagged_only=${flaggedOnly}`
      );
      setCertificates(response.data.certificates);
      setTotalPages(response.data.pages);
      setSelectedCerts([]);
      setSelectAll(false);
    } catch (error) {
      toast.error("Failed to fetch certificates");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResult(null);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(
        `${API}/admin/certificates/search?token=${token}&query=${searchQuery.trim().toUpperCase()}`
      );
      if (response.data.certificate) {
        setSearchResult(response.data.certificate);
        toast.success("Certificate found!");
      } else {
        setSearchResult(null);
        toast.error("No certificate found with that ID");
      }
    } catch (error) {
      setSearchResult(null);
      toast.error("Certificate not found");
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResult(null);
  };

  const toggleSelectCert = (certId) => {
    setSelectedCerts(prev => 
      prev.includes(certId) 
        ? prev.filter(id => id !== certId)
        : [...prev, certId]
    );
  };

  const handleBulkAction = async (action) => {
    if (selectedCerts.length === 0) {
      toast.error("No certificates selected");
      return;
    }

    const actionText = action === "delete" ? "delete" : action === "flag" ? "flag" : "unflag";
    if (!window.confirm(`Are you sure you want to ${actionText} ${selectedCerts.length} certificate(s)?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/certificates/bulk?token=${token}`, {
        certificate_ids: selectedCerts,
        action: action,
        reason: action === "flag" ? "Bulk flagged by admin" : null
      });
      
      toast.success(`${response.data.success_count} certificates ${actionText}ed successfully`);
      if (response.data.failed_count > 0) {
        toast.error(`${response.data.failed_count} failed`);
      }
      fetchCertificates();
    } catch (error) {
      toast.error(`Bulk ${actionText} failed`);
    }
  };

  const handleEdit = (cert) => {
    setEditingCert(cert);
    setEditForm({
      your_name: cert.your_name,
      partner_name: cert.partner_name,
      description: cert.description || ""
    });
  };

  const handleSaveEdit = async () => {
    if (!editingCert) return;
    
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/certificates/${editingCert.certificate_id}?token=${token}`, editForm);
      toast.success("Certificate updated successfully");
      setEditingCert(null);
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update certificate");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFlag = async (certId, reason) => {
    try {
      await axios.post(`${API}/admin/certificates/${certId}/flag?token=${token}`, { reason });
      toast.success("Certificate flagged");
      fetchCertificates();
    } catch (error) {
      toast.error("Failed to flag certificate");
    }
  };

  const handleUnflag = async (certId) => {
    try {
      await axios.post(`${API}/admin/certificates/${certId}/unflag?token=${token}`);
      toast.success("Certificate unflagged");
      fetchCertificates();
    } catch (error) {
      toast.error("Failed to unflag certificate");
    }
  };

  const handleDelete = async (certId) => {
    if (!window.confirm("Are you sure you want to delete this certificate?")) return;
    
    try {
      await axios.delete(`${API}/admin/certificates/${certId}?token=${token}`);
      toast.success("Certificate deleted");
      fetchCertificates();
    } catch (error) {
      toast.error("Failed to delete certificate");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/export?token=${token}&format=csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'certificates.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[#E8A0BF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Edit Modal */}
      {editingCert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2D2D2D]">Edit Certificate</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingCert(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-[#888888] mb-4 font-mono">{editingCert.certificate_id}</p>
            
            <div className="space-y-4">
              <div>
                <Label>Your Name</Label>
                <Input
                  value={editForm.your_name}
                  onChange={(e) => setEditForm({...editForm, your_name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Partner Name</Label>
                <Input
                  value={editForm.partner_name}
                  onChange={(e) => setEditForm({...editForm, partner_name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description</Label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  className="mt-1 w-full h-24 p-2 border rounded-lg text-sm"
                  maxLength={500}
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setEditingCert(null)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-[#E8A0BF] hover:bg-[#D68C85]"
                onClick={handleSaveEdit}
                disabled={isSaving}
              >
                {isSaving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#888888]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by LoBe ID (e.g., LOBE-XXXXXXXX)"
              className="pl-10 h-11"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSearch}
              disabled={isSearching}
              className="bg-[#E8A0BF] hover:bg-[#D68C85] text-white"
              data-testid="admin-search-btn"
            >
              {isSearching ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              <span className="ml-2 hidden sm:inline">Search</span>
            </Button>
            {searchResult && (
              <Button
                onClick={clearSearch}
                variant="outline"
                className="border-[#E8A0BF] text-[#E8A0BF]"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Search Result */}
      {searchResult && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Search Result
          </h4>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-[#2D2D2D]">
                  {searchResult.your_name} & {searchResult.partner_name}
                </p>
                <p className="text-sm text-[#888888] font-mono">{searchResult.certificate_id}</p>
                <p className="text-sm text-[#5A5A5A] mt-1">{searchResult.description}</p>
                {searchResult.transaction_id && (
                  <p className="text-xs text-[#888888] mt-1">
                    Transaction: <span className="font-mono">{searchResult.transaction_id}</span>
                    {searchResult.payment_gateway && (
                      <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                        {searchResult.payment_gateway.toUpperCase()}
                      </span>
                    )}
                  </p>
                )}
                <div className="flex gap-2 mt-2">
                  {searchResult.is_paid ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Paid</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Unpaid</span>
                  )}
                  {searchResult.is_ended && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Ended</span>
                  )}
                  {searchResult.is_flagged && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">Flagged</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(searchResult)}
                  className="text-[#5A5A5A]"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                {searchResult.is_paid && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(`/proof/${searchResult.certificate_id}`, '_blank')}
                    className="text-[#5A5A5A]"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedCerts.length > 0 && (
        <div className="bg-[#E8A0BF]/10 border border-[#E8A0BF] rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm text-[#5A5A5A]">
            <strong>{selectedCerts.length}</strong> certificate(s) selected
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleBulkAction("flag")}
              className="border-orange-400 text-orange-600"
            >
              <Flag className="w-3 h-3 mr-1" />
              Flag All
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleBulkAction("unflag")}
              className="border-green-400 text-green-600"
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              Unflag All
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleBulkAction("delete")}
              className="border-red-400 text-red-600"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete All
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-[#2D2D2D]">
            {flaggedOnly ? "Flagged Certificates" : "All Certificates"}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectAll(!selectAll)}
            className="text-[#5A5A5A]"
          >
            {selectAll ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            <span className="ml-1 text-xs">Select All</span>
          </Button>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchCertificates}
            className="border-[#E8A0BF] text-[#E8A0BF]"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={handleExport}
            className="bg-[#E8A0BF] hover:bg-[#D68C85] text-white"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Certificates List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {certificates.length === 0 ? (
          <div className="p-12 text-center text-[#888888]">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No certificates found</p>
          </div>
        ) : (
          <div className="divide-y divide-[#E2E2E2]">
            {certificates.map((cert) => (
              <div key={cert.id} className={`p-4 hover:bg-[#F4F1EA]/50 transition-colors ${selectedCerts.includes(cert.certificate_id) ? 'bg-[#E8A0BF]/5' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <button 
                      onClick={() => toggleSelectCert(cert.certificate_id)}
                      className="mt-1 text-[#888888] hover:text-[#E8A0BF]"
                    >
                      {selectedCerts.includes(cert.certificate_id) ? (
                        <CheckSquare className="w-5 h-5 text-[#E8A0BF]" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-[#2D2D2D]">
                          {cert.your_name} & {cert.partner_name}
                        </p>
                        {cert.is_flagged && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                            Flagged
                          </span>
                        )}
                        {cert.is_paid ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                            Paid
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                            Unpaid
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#888888] font-mono mb-1">{cert.certificate_id}</p>
                      <p className="text-sm text-[#5A5A5A] line-clamp-2">{cert.description}</p>
                      {cert.transaction_id && (
                        <p className="text-xs text-[#888888] mt-1">
                          Txn: <span className="font-mono">{cert.transaction_id}</span>
                        </p>
                      )}
                      {cert.flag_reason && (
                        <p className="text-sm text-red-600 mt-1">Reason: {cert.flag_reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(cert)}
                      className="text-[#5A5A5A]"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {cert.is_paid && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/proof/${cert.certificate_id}`, '_blank')}
                        className="text-[#5A5A5A]"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    {cert.is_flagged ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnflag(cert.id)}
                        className="text-green-600"
                        title="Unflag"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFlag(cert.id, "Flagged by admin")}
                        className="text-orange-500"
                        title="Flag"
                      >
                        <Flag className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(cert.id)}
                      className="text-red-500"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-[#5A5A5A]">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

// Purchases Tab Component - Purchase Analytics for Auditing
const PurchasesTab = ({ token }) => {
  const [purchases, setPurchases] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    fetchPurchases();
  }, [dateFilter]);

  const fetchPurchases = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/purchases?token=${token}&filter=${dateFilter}`);
      setPurchases(response.data.purchases || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch purchases:', error);
      toast.error("Failed to fetch purchase data");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency) => {
    if (currency === 'INR') {
      return `₹${amount}`;
    }
    return `$${amount}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[#E8A0BF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Revenue (INR)" 
          value={`₹${stats?.total_inr || 0}`}
          icon={DollarSign}
          color="bg-[#4CAF50]"
        />
        <StatCard 
          title="Total Revenue (USD)" 
          value={`$${stats?.total_usd || 0}`}
          icon={DollarSign}
          color="bg-[#2196F3]"
        />
        <StatCard 
          title="Total Purchases" 
          value={stats?.total_count || 0}
          icon={TrendingUp}
          color="bg-[#E8A0BF]"
        />
        <StatCard 
          title="End Relationship Fees" 
          value={stats?.end_fees_count || 0}
          icon={Heart}
          color="bg-[#FF9800]"
        />
      </div>

      {/* Date Filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-[#5A5A5A]">Filter:</span>
        {['all', 'today', 'week', 'month'].map((filter) => (
          <Button
            key={filter}
            variant={dateFilter === filter ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateFilter(filter)}
            className={dateFilter === filter ? 'bg-[#E8A0BF] text-white' : 'border-[#E8A0BF] text-[#E8A0BF]'}
          >
            {filter === 'all' ? 'All Time' : filter.charAt(0).toUpperCase() + filter.slice(1)}
          </Button>
        ))}
      </div>

      {/* Purchases List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-[#E2E2E2]">
          <h3 className="font-semibold text-[#2D2D2D]">Purchase History</h3>
        </div>
        
        {purchases.length === 0 ? (
          <div className="p-12 text-center text-[#888888]">
            <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No purchases found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F1EA]">
                <tr>
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Date</th>
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Certificate ID</th>
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Names</th>
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Transaction ID</th>
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Gateway</th>
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Type</th>
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Amount</th>
                  <th className="text-left p-3 font-medium text-[#5A5A5A]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E2E2]">
                {purchases.map((purchase, index) => (
                  <tr key={index} className="hover:bg-[#F4F1EA]/50">
                    <td className="p-3 text-[#5A5A5A] whitespace-nowrap">{formatDate(purchase.created_at)}</td>
                    <td className="p-3 font-mono text-[#888888] text-xs">{purchase.certificate_id}</td>
                    <td className="p-3 text-[#2D2D2D]">{purchase.names || 'N/A'}</td>
                    <td className="p-3 font-mono text-[#888888] text-xs">{purchase.transaction_id || purchase.payment_id || 'N/A'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        purchase.payment_gateway === 'paypal' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {(purchase.payment_gateway || 'razorpay').toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        purchase.payment_type === 'certificate' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {purchase.payment_type === 'certificate' ? 'New Certificate' : 'End Relationship'}
                      </span>
                    </td>
                    <td className="p-3 font-medium text-[#2D2D2D]">
                      {formatCurrency(purchase.amount || 0, purchase.currency || 'USD')}
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                        Completed
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
